import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { Field, reduxForm, reset } from 'redux-form';
import {
  H1, Button,
} from '@blueprintjs/core';
import { autobind } from 'core-decorators';
import { TextInput, CheckboxInput } from 'components/inputs';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import { AppToaster } from 'components/Toaster';
import {
  getUser, saveUser, onePasswordCreate, onePasswordRotate,
} from 'actions/user';
import { getOrganization } from 'actions/organization';
import { getRoles } from 'actions/role';

import RoleSelect from './input';

class User extends Component {
  constructor(props) {
    super(props);
    this.state = {
      opProcessing: false,
    };
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(getUser(match.params.id));
  }

  componentDidUpdate(prevProps) {
    const { user, dispatch } = this.props;
    const prevOrgId = (prevProps.user.data || {}).organization_id;
    const orgId = (user.data || {}).organization_id;
    if (orgId !== prevOrgId) {
      dispatch(getOrganization(orgId));
      dispatch(getRoles(orgId));
    }
  }

  @autobind
  handleSave(values) {
    const { dispatch, match } = this.props;
    return dispatch(saveUser(match.params.id, {
      name: values.name,
      email: values.email,
      disabled: values.disabled,
      password: values.password ? values.password : undefined,
      phone_number: values.phone_number,
      role_id: values.role ? values.role.role_id : undefined,
    })).then(() => {
      dispatch(reset('user'));
      AppToaster.show({
        icon: 'tick',
        message: (
          <span>
            <strong>
              {values.name}
            </strong>
            : User saved
          </span>
        ),
        intent: 'success',
      });
      return dispatch(getUser(match.params.id));
    });
  }

  @autobind
  handle1PasswordRotation() {
    const { dispatch, match, user } = this.props;
    this.setState({ opProcessing: true });
    return dispatch(onePasswordRotate(match.params.id))
      .then(() => {
        AppToaster.show({
          icon: 'tick',
          message: (
            <span>
              <strong>
                {(user.data || {}).name}
              </strong>
              : Password rotated
            </span>
          ),
          intent: 'success',
        });
        return dispatch(getUser(match.params.id));
      })
      .finally(() => this.setState({ opProcessing: false }));
  }

  @autobind
  handle1PasswordCreation() {
    const { dispatch, match, user } = this.props;
    this.setState({ opProcessing: true });
    return dispatch(onePasswordCreate(match.params.id))
      .then(() => {
        AppToaster.show({
          icon: 'tick',
          message: (
            <span>
              <strong>
                {(user.data || {}).name}
              </strong>
              : 1Password login created
            </span>
          ),
          intent: 'success',
        });
        return dispatch(getUser(match.params.id));
      })
      .finally(() => this.setState({ opProcessing: false }));
  }

  render() {
    const {
      user, submitting, handleSubmit, match, organization,
    } = this.props;
    const { opProcessing } = this.state;
    const userId = parseInt(match.params.id, 10);
    const org = (organization.data
      && (user.data || {}).organization_id === (organization.data || {}).id)
      ? organization.data : {};
    const isOnePasswordManaged = (user.data || {}).onepassword_item_id !== null;
    const isLrmInstaller = ((user.data || {}).email || '').startsWith('lrm_installer+');

    return (
      <div className="container">
        <div className={classNames('columns', { 'bp3-skeleton': user.pending && (user.data || {}).id !== userId })}>
          <form onSubmit={handleSubmit(this.handleSave)} className="column" autoComplete="off">
            <H1>
              {(user.data || {}).name}
              <div className="bp3-text-small bp3-text-muted">
                Registered:&nbsp;
                {DateTime.fromISO((user.data || {}).created).toLocaleString(DateTime.DATETIME_MED)}
                &nbsp;|
                Organization:&nbsp;
                <Link to={`/organizations/${(user.data || {}).organization_id}`}>{org.name || (user.data || {}).organization_id}</Link>
              </div>
            </H1>
            <div className="columns">
              <div className="column">
                <Field
                  component={TextInput}
                  placeholder=""
                  name="email"
                  label="Email Address"
                  type="text"
                />
              </div>
              <div className="column">
                <Field
                  component={TextInput}
                  placeholder=""
                  name="name"
                  label="Name"
                  type="text"
                />
              </div>
            </div>
            <div className="columns">
              <div className="column">
                <Field
                  component={TextInput}
                  placeholder=""
                  name="phone_number"
                  label="Phone Number"
                  type="text"
                />
              </div>
              {isOnePasswordManaged && (
                <div className="column">
                  <div style={{ marginBottom: 5 }}>Password</div>
                  <Button intent="warning" loading={opProcessing} disabled={opProcessing || submitting} type="button" icon="refresh" onClick={this.handle1PasswordRotation}>
                    Rotate password
                  </Button>
                  <div className="bp3-text-small bp3-text-muted m-t-xs">
                    This action will generate a new random password and update the user
                    and associated 1password item accordingly.
                  </div>
                </div>
              )}
              {!isOnePasswordManaged && isLrmInstaller && (
                <div className="column">
                  <div style={{ marginBottom: 5 }}>Password</div>
                  <Button intent="success" loading={opProcessing} disabled={opProcessing || submitting} type="button" icon="key" onClick={this.handle1PasswordCreation}>
                    Create 1password login
                  </Button>
                  <div className="bp3-text-small bp3-text-muted m-t-xs">
                    This action will create a new 1password item using a random password
                    and update user&apos;s current password.
                  </div>
                </div>
              )}
              {!isOnePasswordManaged && !isLrmInstaller && (
                <div className="column">
                  <Field
                    component={TextInput}
                    placeholder=""
                    name="password"
                    label="Password"
                    type="password"
                  />
                </div>
              )}
            </div>
            <div className="columns">
              <div className="column">
                <Field
                  component={CheckboxInput}
                  type="checkbox"
                  id="disable"
                  name="disabled"
                  label="Disabled"
                />
              </div>
              <div className="column">
                <Field
                  component={RoleSelect}
                  name="role"
                />
              </div>
            </div>
            <div className="has-text-right">
              <Button intent="primary" loading={submitting} disabled={submitting} type="submit" icon="tick">
                Save
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

User.propTypes = {
  dispatch: PropTypes.func,
  user: PropTypes.object,
  match: PropTypes.object,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  organization: PropTypes.object,
};

export default connect((state, props) => ({
  user: state.user,
  organization: state.organization,
  initialValues: state.user.data && state.user.data.id === parseInt(props.match.params.id, 10) ? {
    name: state.user.data.name,
    email: state.user.data.email,
    phone_number: state.user.data.phone_number || '',
    disabled: state.user.data.disabled,
    role: state.user.data.role,
  } : undefined,
}))(reduxForm({
  form: 'user',
  enableReinitialize: true,
})(User));
